/**
 * @fileoverview Connect matter-scoped queued Trello work to explicit task editing. The backend owns queue records; Redux refresh signals reconcile accepted changes. `TrelloTaskForm.js` owns selected task details. A queued instruction and an acknowledged remote Trello card remain separate execution state.
 */

import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import api from '../../api';
import ToastContext from '../../ToastContext';
import { useDispatch, useSelector } from 'react-redux';
import TrelloTaskForm from './TrelloTaskForm';
import { setRefreshTrelloTasks } from '../../store/CaseDetails/action';
import { AppButton } from '../shared';
import './QueuedTasks.css';
import AppIcon from "../shared/AppIcon";

const TASK_PANEL_CONFIG = [
{
  key: 'dueDate',
  title: 'With Due Date',
  description: 'Queued tasks tied to a tracked deadline and Trello card creation date.',
  icon: 'pi-flag',
  tone: 'warning',
  matches: (task) => task.TaskType === 'DueDate'
},
{
  key: 'flexible',
  title: 'Without Due Date',
  description: 'Queued tasks that are not anchored to a formal due date.',
  icon: 'pi-list-check',
  tone: 'info',
  matches: (task) => task.TaskType !== 'DueDate'
}];




function normalizeText(value) {
  return String(value || '').trim();
}



function truncateText(text, maxLength = 180) {
  const safeText = normalizeText(text);
  if (safeText.length <= maxLength) {
    return safeText;
  }
  return `${safeText.slice(0, maxLength - 3)}...`;
}



function parseTaskDate(value) {
  if (!value) return null;
  const normalized = typeof value === 'string' && value.includes(' ') && !value.includes('T') ?
  value.replace(' ', 'T') :
  value;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}



function formatTaskDate(value, fallback = 'No schedule date') {
  const date = parseTaskDate(value);
  if (!date) return fallback;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}



function startOfDay(value) {
  if (!value) return null;
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
}



function getTaskStatus(task) {
  if (task?.Popped) {
    return { label: 'Created in Trello', tone: 'success' };
  }

  const scheduleDate = parseTaskDate(task?.Date);
  if (!scheduleDate) {
    return { label: 'Unscheduled', tone: 'warning' };
  }

  const scheduleDay = startOfDay(scheduleDate);
  const today = startOfDay(new Date());

  if (scheduleDay < today) {
    return { label: 'Overdue', tone: 'danger' };
  }

  if (scheduleDay.getTime() === today.getTime()) {
    return { label: 'Due Today', tone: 'warning' };
  }

  return { label: 'Queued', tone: 'info' };
}



function sortQueuedTasks(tasks) {
  return [...tasks].sort(

  (left, right) => {
    if (!!left.Popped !== !!right.Popped) {
      return Number(left.Popped) - Number(right.Popped);
    }

    const leftDate = parseTaskDate(left.Date);
    const rightDate = parseTaskDate(right.Date);

    if (!leftDate && !rightDate) {
      return normalizeText(left.Name).localeCompare(normalizeText(right.Name));
    }

    if (!leftDate) return -1;
    if (!rightDate) return 1;

    return leftDate.getTime() - rightDate.getTime();
  });
}



function TaskPill({ tone = 'neutral', children }) {
  return (
    <span className={`queued-task-pill queued-task-pill--${tone}`}>
            {children}
        </span>);

}



function QueuedTasks({ matterNumber }) {
  const dispatch = useDispatch();
  const matter = useSelector((state) => state.caseDetails.matter);
  const refreshTrelloTasks = useSelector((state) => state.caseDetails.refreshTrelloTasks);
  const showToast = useContext(ToastContext);
  const [queuedTasks, setQueuedTasks] = useState([]);
  const [currentlyEditingTrelloTask, setCurrentlyEditingTrelloTask] = useState(null);
  const [trelloTaskFormVisible, setTrelloTaskFormVisible] = useState(false);

  const activeMatterNumber = matterNumber || matter?.MatterNumber || '';

  const getTasks = useCallback(

  (nextMatterNumber) => {
    if (!nextMatterNumber) {
      setQueuedTasks([]);
      return;
    }

    api.get(`/get_trello_tasks?MatterNumber=${nextMatterNumber}&`)
    .then((response) => {
      if (response.status === 200) {
        setQueuedTasks(Array.isArray(response.data) ? response.data : []);
      } else {
        console.error('Unexpected status code:', response.status);
        showToast({ severity: 'error', summary: 'Error', detail: 'Unexpected status code' });
      }
    })
    .catch((error) => {
      console.error('Error:', error);
      showToast({ severity: 'error', summary: 'Error', detail: 'Unable to load queued tasks' });
    });
  }, [showToast]);

  useEffect(() => {
    getTasks(activeMatterNumber);
  }, [activeMatterNumber, getTasks]);

  useEffect(() => {
    if (!refreshTrelloTasks) {
      return;
    }

    getTasks(activeMatterNumber);
    dispatch(setRefreshTrelloTasks(false));
  }, [activeMatterNumber, dispatch, getTasks, refreshTrelloTasks]);

  const taskSections = useMemo(() => {
    return TASK_PANEL_CONFIG
    .map((section) => ({
      ...section,
      tasks: sortQueuedTasks(queuedTasks.filter(section.matches))
    }))
    .filter((section) => section.tasks.length > 0);
  }, [queuedTasks]);

  const hasQueuedTasks = queuedTasks.length > 0;

  const openNewTaskForm = useCallback(

  () => {
    setCurrentlyEditingTrelloTask(null);
    setTrelloTaskFormVisible(true);
  }, []);

  const openTrelloTaskFormInEditMode = useCallback(

  (queuedTask) => {
    setCurrentlyEditingTrelloTask(queuedTask);
    setTrelloTaskFormVisible(true);
  }, []);

  return (
    <div className="case-queued-tasks">
            <TrelloTaskForm
        substage={matter?.Substage}
        visible={trelloTaskFormVisible}
        setVisible={setTrelloTaskFormVisible}
        currentlyEditing={currentlyEditingTrelloTask} />


            <section className="queued-tasks-overview">
                <div className="queued-tasks-overview-copy">
                    <div className="queued-tasks-overview-eyebrow">Trello Tasks</div>
                    <h2 className="queued-tasks-overview-title">Queued task workflow</h2>
                    <p className="queued-tasks-overview-description">
                        Tasks waiting to be created in Trello, grouped by deadline behavior and ready for quick edits.
                    </p>
                </div>

                <div className="queued-tasks-actions">
                    <AppButton
            variant="secondary"
            icon="pi pi-plus"
            label="Queue Trello Task"
            className="queued-tasks-action-button"
            onClick={openNewTaskForm} />

                </div>
            </section>

            {!hasQueuedTasks &&
      <section className="queued-tasks-empty-state">
                    <div className="queued-tasks-empty-icon">
                        <AppIcon icon="pi pi-list-check" aria-hidden="true" />
                    </div>
                    <div className="queued-tasks-empty-title">No queued Trello tasks</div>
                    <div className="queued-tasks-empty-copy">
                        Queue a task to track upcoming Trello work for this matter here.
                    </div>
                </section>}


            {hasQueuedTasks &&
      <div className="queued-tasks-panel-grid">
                    {taskSections.map((section) =>
        <section key={section.key} className="queued-tasks-panel" data-tone={section.tone}>
                            <div className="queued-tasks-panel-header">
                                <div className="queued-tasks-panel-copy">
                                    <div className="queued-tasks-panel-title-row">
                                        <span className="queued-tasks-panel-icon">
                                            <AppIcon icon={`pi ${section.icon}`} aria-hidden="true" />
                                        </span>
                                        <h3 className="queued-tasks-panel-title">{section.title}</h3>
                                    </div>
                                    <p className="queued-tasks-panel-description">{section.description}</p>
                                </div>
                                <div className="queued-tasks-panel-count">{section.tasks.length}</div>
                            </div>

                            <ul className="queued-tasks-list">
                                {section.tasks.map((task, index) => {
              const status = getTaskStatus(task);
              const hasDetails = normalizeText(task.Details).length > 0;

              return (
                <li key={`${task.id || task.Name}-${index}`} className="queued-tasks-list-item">
                                            <button
                    type="button"
                    className="queued-task-row"
                    data-tone={status.tone}
                    data-popped={task.Popped ? 'true' : 'false'}
                    onClick={() => openTrelloTaskFormInEditMode(task)}>

                                                <div className="queued-task-row-top">
                                                    <div className="queued-task-row-copy">
                                                        <div className="queued-task-row-title">{task.Name || 'Queued task'}</div>
                                                        <div className="queued-task-row-meta">
                                                            <TaskPill tone={status.tone}>{status.label}</TaskPill>
                                                            {task.AttorneyName &&
                          <TaskPill tone="neutral">{task.AttorneyName}</TaskPill>}

                                                        </div>
                                                    </div>

                                                    <div className="queued-task-row-date">
                                                        <span className="queued-task-row-date-label">Card Date</span>
                                                        <span className="queued-task-row-date-value">
                                                            {formatTaskDate(task.Date)}
                                                        </span>
                                                    </div>
                                                </div>

                                                {hasDetails &&
                    <div className="queued-task-row-details">
                                                        {truncateText(task.Details, 220)}
                                                    </div>}


                                                <div className="queued-task-row-footer">
                                                    <span>
                                                        <AppIcon icon="pi pi-calendar" aria-hidden="true" />
                                                        {formatTaskDate(task.Date)}
                                                    </span>
                                                    {task.Deadline &&
                      <span>
                                                            <AppIcon icon="pi pi-flag" aria-hidden="true" />
                                                            Deadline {formatTaskDate(task.Deadline)}
                                                        </span>}

                                                    {task.Popped &&
                      <span>
                                                            <AppIcon icon="pi pi-check-circle" aria-hidden="true" />
                                                            Locked from editing
                                                        </span>}

                                                </div>
                                            </button>
                                        </li>);

            })}
                            </ul>
                        </section>
        )}
                </div>}

        </div>);

}

export default QueuedTasks;
